import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ProxmoxService } from '../../../services/proxmox.service';
import { catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';

@Component({
  selector: 'app-proxmox-api-explorer',
  templateUrl: './proxmox-api-explorer.component.html',
  styleUrls: ['./proxmox-api-explorer.component.scss']
})
export class ProxmoxApiExplorerComponent implements OnInit {
  isLoggedIn: boolean = false;
  clusterStatus: any = null;
  nodes: any[] = [];
  vms: any[] = [];
  storage: any[] = [];
  users: any[] = [];
  pools: any[] = [];
  accessControl: any = null;
  loading: boolean = false;
  error: string = '';
  successMessage: string = '';
  actionLoading: boolean = false;

  constructor(
    private proxmoxService: ProxmoxService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Check if already logged in
    this.isLoggedIn = this.proxmoxService.isAuthenticated();
    
    // If not logged in, redirect to login page
    if (!this.isLoggedIn) {
      this.router.navigate(['/log-in']);
      return;
    }
    
    // Load initial data
    this.loadClusterData();
  }

  /**
   * Handle logout
   */
  onLogout(): void {
    this.proxmoxService.logout();
    this.isLoggedIn = false;
    // Redirect to login page after logout
    this.router.navigate(['/log-in']);
  }

  /**
   * Load cluster data
   */
  loadClusterData(): void {
    this.loading = true;
    this.error = '';
    this.successMessage = '';

    // Create observables for all data requests
    const clusterStatus$ = this.proxmoxService.getClusterStatus().pipe(
      catchError(error => {
        console.error('Error loading cluster status:', error);
        if (error.status !== 401) {
          this.error += 'Error loading cluster status. ';
        }
        return of(null);
      })
    );

    const nodes$ = this.proxmoxService.getNodes().pipe(
      catchError(error => {
        console.error('Error loading nodes:', error);
        if (error.status !== 401) {
          this.error += 'Error loading nodes. ';
        }
        return of(null);
      })
    );

    const vms$ = this.proxmoxService.getVMs().pipe(
      catchError(error => {
        console.error('Error loading VMs:', error);
        if (error.status !== 401) {
          this.error += 'Error loading VMs. ';
        }
        return of(null);
      })
    );

    const storage$ = this.proxmoxService.getStorage().pipe(
      catchError(error => {
        console.error('Error loading storage:', error);
        if (error.status !== 401) {
          this.error += 'Error loading storage. ';
        }
        return of(null);
      })
    );

    const users$ = this.proxmoxService.getUsers().pipe(
      catchError(error => {
        console.error('Error loading users:', error);
        if (error.status !== 401) {
          this.error += 'Error loading users. ';
        }
        return of(null);
      })
    );

    const pools$ = this.proxmoxService.getPools().pipe(
      catchError(error => {
        console.error('Error loading pools:', error);
        if (error.status !== 401) {
          this.error += 'Error loading pools. ';
        }
        return of(null);
      })
    );

    const accessControl$ = this.proxmoxService.getAccessControl().pipe(
      catchError(error => {
        console.error('Error loading access control:', error);
        if (error.status !== 401) {
          this.error += 'Error loading access control. ';
        }
        return of(null);
      })
    );

    // Execute all requests in parallel
    forkJoin({
      clusterStatus: clusterStatus$,
      nodes: nodes$,
      vms: vms$,
      storage: storage$,
      users: users$,
      pools: pools$,
      accessControl: accessControl$
    }).subscribe({
      next: (results) => {
        // Process results
        if (results.clusterStatus) {
          this.clusterStatus = results.clusterStatus.data || null;
        }
        
        if (results.nodes) {
          this.nodes = results.nodes.data || [];
        }
        
        if (results.vms) {
          this.vms = results.vms.data || [];
        }
        
        if (results.storage) {
          this.storage = results.storage.data || [];
        }
        
        if (results.users) {
          this.users = results.users.data || [];
        }
        
        if (results.pools) {
          this.pools = results.pools.data || [];
        }
        
        if (results.accessControl) {
          this.accessControl = results.accessControl.data || null;
        }
        
        this.loading = false;
        
        if (!this.error) {
          this.successMessage = 'Data loaded successfully!';
          // Clear success message after 3 seconds
          setTimeout(() => {
            this.successMessage = '';
          }, 3000);
        }
      },
      error: (error) => {
        console.error('Unexpected error loading data:', error);
        this.loading = false;
        this.error = 'Unexpected error occurred while loading data. Please try again.';
      }
    });
  }

  /**
   * Refresh all data
   */
  refreshData(): void {
    this.loadClusterData();
  }

  /**
   * Show success message
   */
  private showSuccess(message: string): void {
    this.successMessage = message;
    // Clear success message after 3 seconds
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  /**
   * Start a VM
   */
  startVM(node: string, type: string, vmid: number, vmName: string): void {
    if (!confirm(`Are you sure you want to start VM "${vmName}" (ID: ${vmid})?`)) {
      return;
    }

    this.actionLoading = true;
    this.error = '';
    this.successMessage = '';

    this.proxmoxService.startVM(node, type, vmid).subscribe({
      next: (response) => {
        this.actionLoading = false;
        this.showSuccess(`VM "${vmName}" (ID: ${vmid}) start command sent successfully.`);
        // Refresh VM data
        this.loadClusterData();
      },
      error: (error) => {
        this.actionLoading = false;
        this.error = `Error starting VM "${vmName}" (ID: ${vmid}): ${error}`;
        console.error('Error starting VM:', error);
      }
    });
  }

  /**
   * Stop a VM
   */
  stopVM(node: string, type: string, vmid: number, vmName: string): void {
    if (!confirm(`Are you sure you want to stop VM "${vmName}" (ID: ${vmid})?`)) {
      return;
    }

    this.actionLoading = true;
    this.error = '';
    this.successMessage = '';

    this.proxmoxService.stopVM(node, type, vmid).subscribe({
      next: (response) => {
        this.actionLoading = false;
        this.showSuccess(`VM "${vmName}" (ID: ${vmid}) stop command sent successfully.`);
        // Refresh VM data
        this.loadClusterData();
      },
      error: (error) => {
        this.actionLoading = false;
        this.error = `Error stopping VM "${vmName}" (ID: ${vmid}): ${error}`;
        console.error('Error stopping VM:', error);
      }
    });
  }

  /**
   * Reset a VM
   */
  resetVM(node: string, type: string, vmid: number, vmName: string): void {
    if (!confirm(`Are you sure you want to reset VM "${vmName}" (ID: ${vmid})? This will forcefully restart the VM.`)) {
      return;
    }

    this.actionLoading = true;
    this.error = '';
    this.successMessage = '';

    this.proxmoxService.resetVM(node, type, vmid).subscribe({
      next: (response) => {
        this.actionLoading = false;
        this.showSuccess(`VM "${vmName}" (ID: ${vmid}) reset command sent successfully.`);
        // Refresh VM data
        this.loadClusterData();
      },
      error: (error) => {
        this.actionLoading = false;
        this.error = `Error resetting VM "${vmName}" (ID: ${vmid}): ${error}`;
        console.error('Error resetting VM:', error);
      }
    });
  }

  /**
   * Shutdown a VM
   */
  shutdownVM(node: string, type: string, vmid: number, vmName: string): void {
    if (!confirm(`Are you sure you want to shut down VM "${vmName}" (ID: ${vmid})?`)) {
      return;
    }

    this.actionLoading = true;
    this.error = '';
    this.successMessage = '';

    this.proxmoxService.shutdownVM(node, type, vmid).subscribe({
      next: (response) => {
        this.actionLoading = false;
        this.showSuccess(`VM "${vmName}" (ID: ${vmid}) shutdown command sent successfully.`);
        // Refresh VM data
        this.loadClusterData();
      },
      error: (error) => {
        this.actionLoading = false;
        this.error = `Error shutting down VM "${vmName}" (ID: ${vmid}): ${error}`;
        console.error('Error shutting down VM:', error);
      }
    });
  }

  /**
   * Suspend a VM
   */
  suspendVM(node: string, type: string, vmid: number, vmName: string): void {
    if (!confirm(`Are you sure you want to suspend VM "${vmName}" (ID: ${vmid})?`)) {
      return;
    }

    this.actionLoading = true;
    this.error = '';
    this.successMessage = '';

    this.proxmoxService.suspendVM(node, type, vmid).subscribe({
      next: (response) => {
        this.actionLoading = false;
        this.showSuccess(`VM "${vmName}" (ID: ${vmid}) suspend command sent successfully.`);
        // Refresh VM data
        this.loadClusterData();
      },
      error: (error) => {
        this.actionLoading = false;
        this.error = `Error suspending VM "${vmName}" (ID: ${vmid}): ${error}`;
        console.error('Error suspending VM:', error);
      }
    });
  }

  /**
   * Resume a VM
   */
  resumeVM(node: string, type: string, vmid: number, vmName: string): void {
    if (!confirm(`Are you sure you want to resume VM "${vmName}" (ID: ${vmid})?`)) {
      return;
    }

    this.actionLoading = true;
    this.error = '';
    this.successMessage = '';

    this.proxmoxService.resumeVM(node, type, vmid).subscribe({
      next: (response) => {
        this.actionLoading = false;
        this.showSuccess(`VM "${vmName}" (ID: ${vmid}) resume command sent successfully.`);
        // Refresh VM data
        this.loadClusterData();
      },
      error: (error) => {
        this.actionLoading = false;
        this.error = `Error resuming VM "${vmName}" (ID: ${vmid}): ${error}`;
        console.error('Error resuming VM:', error);
      }
    });
  }

  /**
   * Start a node service
   */
  startNodeService(node: string, service: string, serviceName: string): void {
    if (!confirm(`Are you sure you want to start service "${serviceName}" on node "${node}"?`)) {
      return;
    }

    this.actionLoading = true;
    this.error = '';
    this.successMessage = '';

    this.proxmoxService.startNodeService(node, service).subscribe({
      next: (response) => {
        this.actionLoading = false;
        this.showSuccess(`Service "${serviceName}" on node "${node}" start command sent successfully.`);
        // Refresh data
        this.loadClusterData();
      },
      error: (error) => {
        this.actionLoading = false;
        this.error = `Error starting service "${serviceName}" on node "${node}": ${error}`;
        console.error('Error starting node service:', error);
      }
    });
  }

  /**
   * Stop a node service
   */
  stopNodeService(node: string, service: string, serviceName: string): void {
    if (!confirm(`Are you sure you want to stop service "${serviceName}" on node "${node}"?`)) {
      return;
    }

    this.actionLoading = true;
    this.error = '';
    this.successMessage = '';

    this.proxmoxService.stopNodeService(node, service).subscribe({
      next: (response) => {
        this.actionLoading = false;
        this.showSuccess(`Service "${serviceName}" on node "${node}" stop command sent successfully.`);
        // Refresh data
        this.loadClusterData();
      },
      error: (error) => {
        this.actionLoading = false;
        this.error = `Error stopping service "${serviceName}" on node "${node}": ${error}`;
        console.error('Error stopping node service:', error);
      }
    });
  }

  /**
   * Restart a node service
   */
  restartNodeService(node: string, service: string, serviceName: string): void {
    if (!confirm(`Are you sure you want to restart service "${serviceName}" on node "${node}"?`)) {
      return;
    }

    this.actionLoading = true;
    this.error = '';
    this.successMessage = '';

    this.proxmoxService.restartNodeService(node, service).subscribe({
      next: (response) => {
        this.actionLoading = false;
        this.showSuccess(`Service "${serviceName}" on node "${node}" restart command sent successfully.`);
        // Refresh data
        this.loadClusterData();
      },
      error: (error) => {
        this.actionLoading = false;
        this.error = `Error restarting service "${serviceName}" on node "${node}": ${error}`;
        console.error('Error restarting node service:', error);
      }
    });
  }

  /**
   * View node services
   */
  viewNodeServices(node: string): void {
    // This would typically load and display services for the selected node
    // For now, we'll just show an alert
    alert(`Viewing services for node: ${node}\nIn a full implementation, this would show a detailed view of node services.`);
  }

  /**
   * View storage content
   */
  viewStorageContent(storage: string, storageName: string): void {
    // This would typically load and display content for the selected storage
    // For now, we'll just show an alert
    alert(`Viewing content for storage: ${storageName}\nIn a full implementation, this would show a detailed view of storage content.`);
  }

  /**
   * View user details
   */
  viewUserDetails(userid: string, username: string): void {
    // This would typically load and display details for the selected user
    // For now, we'll just show an alert
    alert(`Viewing details for user: ${username}\nIn a full implementation, this would show a detailed view of user information.`);
  }

  /**
   * View group details
   */
  viewGroupDetails(groupid: string, groupname: string): void {
    // This would typically load and display details for the selected group
    // For now, we'll just show an alert
    alert(`Viewing details for group: ${groupname}\nIn a full implementation, this would show a detailed view of group information.`);
  }

  /**
   * View role details
   */
  viewRoleDetails(roleid: string, rolename: string): void {
    // This would typically load and display details for the selected role
    // For now, we'll just show an alert
    alert(`Viewing details for role: ${rolename}\nIn a full implementation, this would show a detailed view of role information.`);
  }

  /**
   * Format bytes to human readable format
   */
  formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  /**
   * Format uptime to human readable format
   */
  formatUptime(seconds: number): string {
    const days = Math.floor(seconds / (24 * 3600));
    seconds %= 24 * 3600;
    const hours = Math.floor(seconds / 3600);
    seconds %= 3600;
    const minutes = Math.floor(seconds / 60);
    seconds %= 60;

    let result = '';
    if (days > 0) result += days + 'd ';
    if (hours > 0) result += hours + 'h ';
    if (minutes > 0) result += minutes + 'm ';
    if (seconds > 0) result += seconds + 's';

    return result.trim();
  }

  /**
   * Get VM status badge class
   */
  getVMStatusClass(status: string): string {
    switch (status) {
      case 'running':
        return 'badge-success';
      case 'stopped':
        return 'badge-danger';
      case 'paused':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  }
}